import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { fetchYardChecksByFacility, YardChecksResponse } from 'api/yardCheck';
import { YardGeometryProps } from 'api/yardGeometry';
import { Facility } from '../../utils/facilityStorage';
import YardChecksSummaryCard from './YardChecksSummaryCard';
import YardChecksNotSeenCard from './YardChecksNotSeenCard';
import YardChecksSpotGrid, { YardSpotSelection } from './YardChecksSpotGrid';
import YardSpotDetailModal from './YardSpotDetailModal';

interface YardChecksOverviewProps {
  selectedFacility: Facility | null;
  geometry: GeoJSON.FeatureCollection<GeoJSON.Geometry, YardGeometryProps> | null;
}

const YardChecksOverview = ({ selectedFacility, geometry }: YardChecksOverviewProps) => {
  const [data, setData] = useState<YardChecksResponse | null>(null);
  const [asOf, setAsOf] = useState<Date>(() => new Date());
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selection, setSelection] = useState<YardSpotSelection | null>(null);

  const loadYardChecks = useCallback(async () => {
    if (!selectedFacility) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchYardChecksByFacility(selectedFacility.id);
      setData(result);
      setAsOf(new Date());
    } catch (err: any) {
      setError(err.message || 'Failed to load yard checks');
    } finally {
      setLoading(false);
    }
  }, [selectedFacility]);

  // Fetch yard checks whenever the selected facility changes
  useEffect(() => {
    void loadYardChecks();
  }, [loadYardChecks]);

  const hasRtlsData = Boolean(
    data?.facilityZones.some((zone) => zone.yardSpots.some((spot) => spot.latestHistory))
  );

  return (
    <div>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Last refresh{' '}
          {asOf.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
        </Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={loadYardChecks} disabled={loading || !selectedFacility}>
              <Refresh />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex justify-center items-center py-16">
          <CircularProgress />
        </div>
      )}

      {!loading && !error && data && data.facilityZones.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          <Typography variant="body1" color="text.secondary">
            No yard spots found for this facility.
          </Typography>
        </div>
      )}

      {!loading && !error && data && data.facilityZones.length > 0 && !hasRtlsData && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          <Typography variant="body1" color="text.secondary">
            No data found for the facility.
          </Typography>
        </div>
      )}

      {data && hasRtlsData && (
        <>
          <Grid container spacing={3} sx={{ mb: 3, alignItems: 'stretch' }}>
            <Grid item xs={12} md={5}>
              <YardChecksSummaryCard summary={data.summary} />
            </Grid>
            <Grid item xs={12} md={7}>
              <YardChecksNotSeenCard
                facilityZones={data.facilityZones}
                now={asOf}
                onSelectSpot={setSelection}
              />
            </Grid>
          </Grid>

          <YardChecksSpotGrid
            facilityZones={data.facilityZones}
            now={asOf}
            onSelectSpot={setSelection}
          />
        </>
      )}

      <YardSpotDetailModal
        selection={selection}
        geometry={geometry}
        now={asOf}
        onClose={() => setSelection(null)}
      />
    </div>
  );
};

export default YardChecksOverview;
